import React, { useState } from 'react';
import { HistoryPesee } from '@/services/historyService';

export interface ExportField {
  key: string;
  label: string;
  category: string;
  required?: boolean;
  defaultSelected?: boolean;
  getValue: (weighing: HistoryPesee) => string | number;
}

export interface FieldSelectorProps {
  weighings: HistoryPesee[];
  selectedFields: string[];
  onFieldsChange: (fields: string[]) => void;
  availableFields: ExportField[];
}

const FieldSelector: React.FC<FieldSelectorProps> = ({
  weighings,
  selectedFields,
  onFieldsChange,
  availableFields
}) => {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['Informations de base']));

  // Grouper les champs par catégorie
  const fieldsByCategory = availableFields.reduce((acc, field) => {
    if (!acc[field.category]) {
      acc[field.category] = [];
    }
    acc[field.category].push(field);
    return acc;
  }, {} as Record<string, ExportField[]>);

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const toggleField = (fieldKey: string) => {
    if (availableFields.find(f => f.key === fieldKey)?.required) {
      return; // Ne pas permettre de désélectionner les champs requis
    }

    const newSelected = selectedFields.includes(fieldKey)
      ? selectedFields.filter(f => f !== fieldKey)
      : [...selectedFields, fieldKey];
    
    onFieldsChange(newSelected);
  };

  const selectAllInCategory = (category: string) => {
    const categoryFields = fieldsByCategory[category];
    const nonRequiredFields = categoryFields.filter(f => !f.required);
    const newSelected = [...selectedFields];
    
    nonRequiredFields.forEach(field => {
      if (!newSelected.includes(field.key)) {
        newSelected.push(field.key);
      }
    });
    
    onFieldsChange(newSelected);
  };

  const deselectAllInCategory = (category: string) => {
    const categoryFields = fieldsByCategory[category];
    const nonRequiredFields = categoryFields.filter(f => !f.required);
    const newSelected = selectedFields.filter(fieldKey => 
      !nonRequiredFields.some(f => f.key === fieldKey)
    );
    
    onFieldsChange(newSelected);
  };

  const selectAllFields = () => {
    const allFields = availableFields.filter(f => !f.required).map(f => f.key);
    onFieldsChange([...selectedFields, ...allFields.filter(f => !selectedFields.includes(f))]);
  };

  const deselectAllFields = () => {
    const requiredFields = availableFields.filter(f => f.required).map(f => f.key);
    onFieldsChange(requiredFields);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 max-h-96 overflow-y-auto">
      <div className="flex items-center justify-between mb-4 sticky top-0 bg-white pb-2">
        <h3 className="text-lg font-semibold text-gray-900">
          Sélection des champs à exporter
        </h3>
        <div className="flex space-x-2">
          <button
            onClick={selectAllFields}
            className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
          >
            Tout sélectionner
          </button>
          <button
            onClick={deselectAllFields}
            className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            Tout désélectionner
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {Object.entries(fieldsByCategory).map(([category, fields]) => (
          <div key={category} className="border border-gray-200 rounded-lg">
            <div
              className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => toggleCategory(category)}
            >
              <div className="flex items-center">
                <i className={`fas fa-chevron-${expandedCategories.has(category) ? 'down' : 'right'} text-gray-500 mr-2`}></i>
                <span className="font-medium text-gray-900">{category}</span>
                <span className="ml-2 text-sm text-gray-500">
                  ({fields.filter(f => selectedFields.includes(f.key)).length}/{fields.length})
                </span>
              </div>
              <div className="flex space-x-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    selectAllInCategory(category);
                  }}
                  className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                >
                  Tout
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deselectAllInCategory(category);
                  }}
                  className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                >
                  Aucun
                </button>
              </div>
            </div>

            {expandedCategories.has(category) && (
              <div className="p-3 space-y-2">
                {fields.map((field) => (
                  <label
                    key={field.key}
                    className={`flex items-center space-x-3 p-2 rounded ${
                      field.required 
                        ? 'bg-blue-50 border border-blue-200' 
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedFields.includes(field.key)}
                      onChange={() => toggleField(field.key)}
                      disabled={field.required}
                      className={`w-4 h-4 rounded border-gray-300 ${
                        field.required 
                          ? 'text-blue-600 bg-blue-50 border-blue-300' 
                          : 'text-blue-600 focus:ring-blue-500'
                      }`}
                    />
                    <div className="flex-1">
                      <span className={`text-sm ${field.required ? 'font-medium text-blue-900' : 'text-gray-900'}`}>
                        {field.label}
                      </span>
                      {field.required && (
                        <span className="ml-2 text-xs text-blue-600">(Requis)</span>
                      )}
                    </div>
                    {/* Aperçu de la valeur pour le premier élément */}
                    {weighings.length > 0 && (
                      <span className="text-xs text-gray-500 max-w-32 truncate">
                        {String(field.getValue(weighings[0])).substring(0, 20)}
                        {String(field.getValue(weighings[0])).length > 20 ? '...' : ''}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-4 pt-3 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>
            {selectedFields.length} champ{selectedFields.length > 1 ? 's' : ''} sélectionné{selectedFields.length > 1 ? 's' : ''}
          </span>
          <span>
            {weighings.length} pesée{weighings.length > 1 ? 's' : ''} à exporter
          </span>
        </div>
      </div>
    </div>
  );
};

export default FieldSelector;
